import React from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PreviewIcon from '@mui/icons-material/Preview';
import CancelIcon from '@mui/icons-material/Cancel';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {useTranslation} from 'react-i18next';
import type {ContractListItem} from '../../../types/contract';
import {isContractEndingSoon, useContractsTableMenu} from '../../../hooks/useContracts';

interface ContractsTableProps {
    contracts: ContractListItem[];
    loading: boolean;
    error?: string;
    hasBranchSelected: boolean;
    searchQuery: string;
    onEdit: (contract: ContractListItem) => void;
    onView: (contract: ContractListItem) => void;
    onTerminate: (contract: ContractListItem) => void;
    onDownloadPdf: (contract: ContractListItem) => void;
    onDownloadWord: (contract: ContractListItem) => void;
    onPreview: (contract: ContractListItem) => void;
    downloadingContractId: string | null;
}

const isActiveStatus = (status: string): boolean => status.toLowerCase() === 'active';

const formatEUR = (value: string | undefined): string => {
    if (value === undefined || value === null || value === '') return '-';
    const num = Number(String(value).replace(/[^0-9.-]+/g, ''));
    if (!isFinite(num)) return String(value);
    try {
        return new Intl.NumberFormat(undefined, {style: 'currency', currency: 'EUR'}).format(num);
    } catch (e) {
        return `€${num.toFixed(2)}`;
    }
};

const ContractsTable: React.FC<ContractsTableProps> = ({
                                                           contracts,
                                                           loading,
                                                           error,
                                                           hasBranchSelected,
                                                           searchQuery,
                                                           onEdit,
                                                           onView,
                                                           onTerminate,
                                                           onDownloadPdf,
                                                           onDownloadWord,
                                                           onPreview,
                                                           downloadingContractId,
                                                       }) => {
    const {t} = useTranslation();
    const {anchorEl, selectedRowId, open, handleMenuClick, handleMenuClose, handleMenuAction} = useContractsTableMenu();

    return (
        <>
            {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
            <Box sx={{overflowX: 'auto'}}>
                <Table sx={{'& .MuiTableCell-root': {whiteSpace: 'normal', wordBreak: 'break-word'}}}
                       aria-label={t('contracts.title')}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('contracts.column.contractEmployee')}</TableCell>
                            <TableCell>{t('contracts.column.positionBranch')}</TableCell>
                            <TableCell>{t('contracts.column.period')}</TableCell>
                            <TableCell>{t('contracts.column.baseSalary')}</TableCell>
                            <TableCell>{t('hr.branches.table.header.status')}</TableCell>
                            <TableCell align="center">{t('workEvidence.approval.table.header.action')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            Array.from({length: 5}).map((_, i) => (
                                <TableRow key={`skeleton-${i}`}>
                                    <TableCell><Skeleton variant="text"/></TableCell>
                                    <TableCell><Skeleton variant="text"/></TableCell>
                                    <TableCell><Skeleton variant="text"/></TableCell>
                                    <TableCell><Skeleton variant="text"/></TableCell>
                                    <TableCell><Skeleton variant="text" width={60}/></TableCell>
                                    <TableCell align="center"><Skeleton variant="circular" width={24} height={24}/></TableCell>
                                </TableRow>
                            ))
                        ) : contracts.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{py: 6}}>
                                    <DescriptionOutlinedIcon sx={{fontSize: 48, color: 'text.disabled', mb: 1}}/>
                                    {!hasBranchSelected ? (
                                        <Typography variant="body1" color="text.secondary">
                                            {t('contracts.table.selectBranchPrompt')}
                                        </Typography>
                                    ) : searchQuery.trim() ? (
                                        <>
                                            <Typography variant="body1" color="text.secondary">
                                                {t('contracts.table.noResultsFor', {query: searchQuery})}
                                            </Typography>
                                            <Typography variant="body2" color="text.disabled">
                                                {t('contracts.table.tryDifferentSearch')}
                                            </Typography>
                                        </>
                                    ) : (
                                        <Typography variant="body1" color="text.secondary">
                                            {t('contracts.table.noContractsFound')}
                                        </Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        ) : (
                            contracts.map((contract) => {
                                const active = isActiveStatus(contract.status);
                                const endingSoon = isContractEndingSoon(contract);
                                return (
                                    <TableRow hover key={contract.contractId}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>
                                                {contract.contractNumber}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {contract.firstName} {contract.lastName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {contract.jobPosition}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {contract.tenantName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {contract.startDate} – {contract.endDate || t('contracts.period.openEnded')}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>
                                                {formatEUR(contract.baseAmount)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {contract.salaryBasis}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {endingSoon ? (
                                                <Chip
                                                    label={t('contracts.status.endingSoon')}
                                                    size="small"
                                                    color="warning"
                                                    icon={<AccessTimeIcon/>}
                                                    variant="outlined"
                                                />
                                            ) : (
                                                <Chip
                                                    label={contract.status}
                                                    size="small"
                                                    color={active ? 'success' : 'error'}
                                                    icon={active ? <CheckCircleIcon/> : undefined}
                                                    variant="outlined"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                aria-label="more"
                                                id={`contract-action-button-${contract.contractId}`}
                                                aria-controls={open && selectedRowId === contract.contractId ? `contract-action-menu-${contract.contractId}` : undefined}
                                                aria-expanded={open && selectedRowId === contract.contractId ? 'true' : undefined}
                                                aria-haspopup="true"
                                                onClick={(e) => handleMenuClick(e, contract.contractId)}
                                                disabled={downloadingContractId === contract.contractId}
                                            >
                                                {downloadingContractId === contract.contractId
                                                    ? <CircularProgress size={24} aria-label={t('contracts.download.inProgress')}/>
                                                    : <MoreVertIcon/>}
                                            </IconButton>
                                            <Menu
                                                id={`contract-action-menu-${contract.contractId}`}
                                                anchorEl={anchorEl}
                                                open={open && selectedRowId === contract.contractId}
                                                onClose={handleMenuClose}
                                                slotProps={{
                                                    paper: {
                                                        style: {
                                                            maxHeight: 48 * 4.5,
                                                            width: '22ch',
                                                        },
                                                    },
                                                }}
                                            >
                                                <MenuItem onClick={() => handleMenuAction(() => onEdit(contract))}>
                                                    <ListItemIcon><EditIcon fontSize="small"
                                                                            color="primary"/></ListItemIcon>
                                                    <ListItemText>{t('common.actions.edit')}</ListItemText>
                                                </MenuItem>
                                                <MenuItem onClick={() => handleMenuAction(() => onView(contract))}>
                                                    <ListItemIcon><VisibilityIcon fontSize="small"
                                                                                  color="info"/></ListItemIcon>
                                                    <ListItemText>{t('common.actions.view')}</ListItemText>
                                                </MenuItem>
                                                <MenuItem
                                                    onClick={() => handleMenuAction(() => onPreview(contract))}>
                                                    <ListItemIcon><PreviewIcon fontSize="small"/></ListItemIcon>
                                                    <ListItemText>{t('contracts.actions.preview')}</ListItemText>
                                                </MenuItem>
                                                <MenuItem
                                                    onClick={() => handleMenuAction(() => onDownloadPdf(contract))}>
                                                    <ListItemIcon><PictureAsPdfIcon
                                                        fontSize="small"/></ListItemIcon>
                                                    <ListItemText>{t('contracts.actions.downloadPdf')}</ListItemText>
                                                </MenuItem>
                                                <MenuItem
                                                    onClick={() => handleMenuAction(() => onDownloadWord(contract))}>
                                                    <ListItemIcon><DescriptionOutlinedIcon
                                                        fontSize="small"/></ListItemIcon>
                                                    <ListItemText>{t('contracts.actions.downloadWord')}</ListItemText>
                                                </MenuItem>
                                                <Divider/>
                                                <MenuItem
                                                    onClick={() => handleMenuAction(() => onTerminate(contract))}
                                                    disabled={!active}
                                                >
                                                    <ListItemIcon><CancelIcon fontSize="small"
                                                                              color={active ? 'error' : 'disabled'}/></ListItemIcon>
                                                    <ListItemText>{t('contracts.actions.terminate')}</ListItemText>
                                                </MenuItem>
                                            </Menu>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </Box>
        </>
    );
};

export default ContractsTable;
